import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ArrowUpRight, Mail, Menu, Play, X } from "lucide-react";
import { HeroMonogram } from "@/components/HeroMonogram";
import Admin from "@/pages/Admin";
import { API, mediaUrl, youtubeEmbed, youtubeId, youtubeThumb, youtubeThumbFallback } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

// text is stored as plain strings: newlines break lines, *stars* mark the accent italic
export const Rich = ({ value = "" }) => (
  <>
    {String(value).split("\n").map((line, lineIndex) => (
      <span key={lineIndex}>
        {lineIndex > 0 && <br />}
        {line.split(/(\*[^*]+\*)/).map((part, partIndex) =>
          part.startsWith("*") && part.endsWith("*") && part.length > 2 ? <em key={partIndex}>{part.slice(1, -1)}</em> : part
        )}
      </span>
    ))}
  </>
);

const CATEGORIES = [
  ["motion", "Motion Graphics"],
  ["long", "Long Form"],
  ["short", "Short Form"],
];

const DEFAULT_PORTRAIT = "https://images.unsplash.com/photo-1594201510970-f549e4a6e96d?crop=entropy&cs=srgb&fm=jpg&w=560&h=740&fit=crop&q=85";

const ProjectMedia = ({ project, testId, loop = false }) => {
  const [playing, setPlaying] = useState(false);
  const [opened, setOpened] = useState(false);
  const ytId = youtubeId(project.video);

  if (ytId) {
    if (opened) {
      return (
        <iframe
          className="project-embed"
          src={youtubeEmbed(ytId)}
          title={project.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          data-testid={testId}
        />
      );
    }
    return (
      <>
        <img
          src={mediaUrl(project.image) || youtubeThumb(ytId)}
          alt={project.title}
          loading="lazy"
          onError={(event) => { event.currentTarget.src = youtubeThumbFallback(ytId); }}
          data-testid={`${testId}-poster`}
        />
        <button type="button" className="project-play-overlay project-embed-play" onClick={() => setOpened(true)} aria-label={`Play ${project.title}`} data-testid={`${testId}-play`}>
          <Play size={22} fill="currentColor" />
        </button>
      </>
    );
  }

  return (
    <>
      <video
        controls={!loop}
        muted
        playsInline
        preload="metadata"
        loop={loop}
        autoPlay={loop}
        poster={mediaUrl(project.image)}
        src={mediaUrl(project.video)}
        aria-label={`${project.title} demo clip`}
        data-testid={testId}
        onPlaying={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      {!loop && !playing && <span className="project-play-overlay"><Play size={22} fill="currentColor" /></span>}
    </>
  );
};

function Home() {
  const [content, setContent] = useState(null);
  const [activeCategory, setActiveCategory] = useState("motion");
  const [menuOpen, setMenuOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", project: "", timeline: "", message: "" });

  useEffect(() => {
    axios.get(`${API}/content`).then(({ data }) => setContent(data)).catch(() => setContent(null));
  }, []);

  const update = (key) => (event) => setForm({ ...form, [key]: event.target.value });
  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.message) {
      setError("Please add your name, email, and a short brief.");
      return;
    }
    try {
      await axios.post(`${API}/inquiries`, form);
      setSent(true);
    } catch {
      setError(`Something went wrong. Please try again or email ${text.email}.`);
    }
  };

  const text = content?.text || {};
  const tools = content?.tools || [];
  const projects = content?.projects?.[activeCategory] || [];
  const marquee = tools.length ? [...tools, ...tools] : [];

  return (
    <div className="site-shell">
      <header className="site-nav" data-testid="site-navigation">
        <a className="wordmark" href="#top" data-testid="wordmark-link"><span className="wordmark-mark">{(text.brand || "C").charAt(0)}</span> {text.brand}</a>
        <nav className={menuOpen ? "nav-links nav-open" : "nav-links"} data-testid="primary-navigation">
          <a href="#work" data-testid="nav-work-link" onClick={() => setMenuOpen(false)}>Work</a>
          <a href="#about" data-testid="nav-about-link" onClick={() => setMenuOpen(false)}>About</a>
          <a href="#contact" data-testid="nav-contact-link" onClick={() => setMenuOpen(false)}>Contact</a>
        </nav>
        <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu" data-testid="mobile-menu-button">{menuOpen ? <X size={20} /> : <Menu size={20} />}</button>
      </header>

      <main id="top">
        <HeroMonogram monogram={text.monogram} plateName={text.plateName} />

        <section className="tools-strip" data-testid="tools-strip" aria-label="Creative tools">
          <div className="tools-track">{marquee.map((tool, index) => <span className="tool-chip" key={`${tool.name}-${index}`} data-testid={`tool-chip-${index}`}><span className="tool-symbol">{tool.symbol}</span>{tool.name}</span>)}</div>
        </section>

        <section id="work" className="work-section section-pad" data-testid="selected-work-section">
          <div className="section-top"><p className="eyebrow" data-testid="selected-work-label">{text.workLabel}</p><span className="section-rule" /></div>
          <h2 className="section-title" data-testid="selected-work-heading"><Rich value={text.workTitle} /></h2>
          <div className="category-tabs" role="tablist" data-testid="portfolio-category-tabs">
            {CATEGORIES.map(([key, label]) => <button className={activeCategory === key ? "category-tab active" : "category-tab"} role="tab" aria-selected={activeCategory === key} onClick={() => setActiveCategory(key)} key={key} data-testid={`category-${key}-tab`}>{label}</button>)}
          </div>
          <div className={`project-grid reference-grid ${activeCategory === "short" ? "short-grid" : ""}`} data-testid={`project-grid-${activeCategory}`}>
            {projects.map((project, index) => {
              const no = String(index + 1).padStart(2, "0");
              return (
                <article className="project" key={project.id || no} data-testid={`project-${activeCategory}-${no}`}>
                  <div className="project-image" data-testid={`project-${activeCategory}-${no}-media`}>
                    <ProjectMedia project={project} loop={activeCategory === "motion"} testId={`project-${activeCategory}-${no}-video`} />
                  </div>
                  <div className="project-meta"><span className="project-no">{no}</span><div><h3 data-testid={`project-${activeCategory}-${no}-title`}>{project.title}</h3><p>{project.type}</p></div><div className="project-detail"><span>{project.year}</span><span>{project.role}</span></div></div>
                </article>
              );
            })}
          </div>
        </section>

        <section id="about" className="about-section section-pad" data-testid="about-section">
          <div className="section-top"><p className="eyebrow" data-testid="about-label">{text.aboutLabel}</p><span className="section-rule" /></div>
          <div className="about-grid">
            <div className="about-lead">
              <h2 className="section-title" data-testid="about-heading"><Rich value={text.aboutTitle} /></h2>
              <figure className="about-portrait" data-testid="about-portrait">
                <img src={mediaUrl(text.aboutPhoto) || DEFAULT_PORTRAIT} alt={text.plateName || "Portrait"} loading="lazy" />
              </figure>
            </div>
            <div className="about-copy">
              <p data-testid="about-bio">{text.aboutBio}</p>
              <p>{text.aboutSecondary}</p>
              <a className="text-cta" href={`mailto:${text.email || ""}`} data-testid="about-email-link"><Mail size={15} /> Say hello <ArrowUpRight size={16} /></a>
            </div>
            <div className="about-facts" data-testid="about-facts"><span>Focus</span><strong>{text.aboutFocus}</strong><span>Also making</span><strong>{text.aboutAlso}</strong><span>Currently</span><strong>{text.aboutCurrently}</strong></div>
          </div>
        </section>

        <section id="contact" className="contact-section section-pad" data-testid="contact-section">
          <div className="contact-heading">
            <p className="eyebrow light"><span className="red-dot" /> {text.contactLabel}</p>
            <h2 data-testid="contact-heading"><Rich value={text.contactTitle} /></h2>
            <p>{text.contactIntro}</p>
          </div>
          {sent ? (
            <div className="success-state" data-testid="inquiry-success-message">
              <span className="success-mark">✓</span>
              <h3>Message received.</h3>
              <p>Thanks for reaching out — I’ll be in touch soon.</p>
              <button className="text-cta light-cta" onClick={() => setSent(false)} data-testid="send-another-button">Send another <ArrowUpRight size={16} /></button>
            </div>
          ) : (
            <form className="inquiry-form" onSubmit={submit} data-testid="inquiry-form">
              <div className="form-row">
                <label>Name <Input value={form.name} onChange={update("name")} placeholder="Your name" autoComplete="name" data-testid="inquiry-name-input" /></label>
                <label>Email <Input type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" autoComplete="email" data-testid="inquiry-email-input" /></label>
              </div>
              <div className="form-row">
                <label>Project <Input value={form.project} onChange={update("project")} placeholder="A short title" data-testid="inquiry-project-input" /></label>
                <label>Timeline <Input value={form.timeline} onChange={update("timeline")} placeholder="When do you need it?" data-testid="inquiry-timeline-input" /></label>
              </div>
              <label>Tell me about it <Textarea value={form.message} onChange={update("message")} placeholder="The story, the feeling, the rough edges…" rows={4} data-testid="inquiry-message-input" /></label>
              {error && <p className="form-error" role="alert" data-testid="inquiry-error-message">{error}</p>}
              <Button type="submit" className="submit-button" data-testid="inquiry-form-submit-button">Send inquiry <ArrowUpRight size={16} /></Button>
            </form>
          )}
        </section>
      </main>
      <footer className="site-footer"><span data-testid="footer-copyright">{text.footerLeft}</span><span>{text.footerRight}</span><a href="#top" data-testid="back-to-top-link">Back to top ↑</a></footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
